import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Button, TextField, Grid,
  MenuItem, Pagination, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  FormControl, InputLabel, Select, OutlinedInput, IconButton, useTheme, useMediaQuery,
  Fab, Stack, Tabs, Tab, Collapse, Card
} from '@mui/material';
import { Visibility, GetApp, Check, Edit, Add, CloudUpload, Delete, FilterList } from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { fetchSamples, acceptSample, createSample } from '../store/sampleSlice';
import api from '../services/api';
import BulkUpload from '../components/BulkUpload';
import SampleCard from '../components/SampleCard';
import DeletedSamples from './DeletedSamples';
import { formatToIST } from '../utils/dateUtils';

const SampleList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { list: samples, loading, pagination } = useSelector(state => state.samples);
  const user = useSelector(state => state.auth.user);
  const { showAllDates } = useSelector(state => state.settings);
  
  const [filters, setFilters] = useState({
    labId: '',
    sampleName: '',
    department: '',
    regularity: '',
    statusFilter: searchParams.get('statusFilter') || '',
    page: 1
  });
  const [showFilters, setShowFilters] = useState(false);

  const [acceptDialog, setAcceptDialog] = useState(false);
  const [selectedSample, setSelectedSample] = useState(null);
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [acceptData, setAcceptData] = useState({
    storageTemperature: ''
  });
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editSample, setEditSample] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const SampleSchema = Yup.object().shape({
    labId: Yup.string().required('Lab ID is required').min(3, 'Lab ID must be at least 3 characters'),
    sampleName: Yup.string().required('Sample name is required').min(2, 'Sample name must be at least 2 characters'),
    storageTemperature: Yup.string().required('Storage temperature is required'),
    regularity: Yup.string().required('Regularity is required'),
    departments: Yup.array().min(1, 'At least one department must be selected').required('Departments are required')
  });

  useEffect(() => {
    const statusFilter = searchParams.get('statusFilter');
    if (statusFilter) {
      setFilters(prev => ({ ...prev, statusFilter }));
    }
  }, [searchParams]);

  useEffect(() => {
    dispatch(fetchSamples(filters));
  }, [dispatch, filters]);

  useEffect(() => {
    const handleRefresh = () => {
      if (tabValue === 0) {
        dispatch(fetchSamples(filters));
      }
    };
    window.addEventListener('refreshActiveSamples', handleRefresh);
    return () => window.removeEventListener('refreshActiveSamples', handleRefresh);
  }, [dispatch, filters, tabValue]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value, page: 1 }));
  };

  const handlePageChange = (event, page) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const getStatusColor = (status) => {
    const colors = {
      'Pending': 'default',
      'Accepted': 'primary',
      'Rejected': 'error',
      'InProgress': 'warning',
      'Completed': 'success'
    };
    return colors[status] || 'default';
  };

  const downloadExcel = () => {
    const params = new URLSearchParams(filters);
    const baseURL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000';
    const token = localStorage.getItem('token');
    
    fetch(`${baseURL}/api/reports/excel?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      return response.blob();
    })
    .then(blob => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Movement-Register-${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    })
    .catch(error => console.error('Download failed:', error));
  };

  const handleAcceptSample = async () => {
    setActionError('');
    if (!acceptData.storageTemperature) {
      setActionError('Please select storage temperature');
      return;
    }
    
    try {
      await dispatch(acceptSample({ 
        id: selectedSample._id, 
        ...acceptData 
      })).unwrap();
      setAcceptDialog(false);
      setSelectedSample(null);
      setAcceptData({ storageTemperature: '' });
      dispatch(fetchSamples(filters));
    } catch (error) {
      setActionError(error.message || 'Failed to accept sample');
    }
  };

  const handleDeleteSample = async (sampleId) => {
    if (window.confirm('Are you sure you want to move this sample to deleted samples?')) {
      try {
        await api.delete(`/samples/${sampleId}`);
        setSuccess('Sample moved to deleted samples!');
        dispatch(fetchSamples(filters));
      } catch (error) {
        setActionError(error.response?.data?.message || 'Failed to delete sample');
      }
    }
  };

  const handleAcceptClick = (sample) => {
    setSelectedSample(sample);
    setAcceptData({ 
      storageTemperature: sample.storageTemperature || ''
    });
    setAcceptDialog(true);
    setActionError('');
  };

  const handleEditClick = (sample) => {
    setEditSample(sample);
    setEditDialog(true);
  };

  const refreshDashboardStats = () => {
    // Trigger dashboard refresh by dispatching a custom event
    window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
  };



  const canAcceptSample = (sample) => {
    return user?.role === 'HOD' && sample.departmentAssignments?.some(
      da => da.department === user.department && da.status === 'Pending'
    );
  };



  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="h4">Sample Management</Typography>
        {['Admin', 'Sample Collector'].includes(user?.role) && tabValue === 0 && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              variant="outlined" 
              startIcon={<Add />} 
              onClick={() => navigate('/samples/create')}
              sx={{ 
                minWidth: { xs: 'auto', sm: 'auto' },
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Sample</Box>
            </Button>
            <Button 
              variant="outlined" 
              startIcon={<CloudUpload />} 
              onClick={() => setBulkUploadOpen(true)}
              sx={{ 
                minWidth: { xs: 'auto', sm: 'auto' },
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bulk Upload</Box>
            </Button>
            <Button 
              variant="contained" 
              startIcon={<GetApp />} 
              onClick={downloadExcel}
              sx={{ 
                minWidth: { xs: 'auto', sm: 'auto' },
                '& .MuiButton-startIcon': { mr: { xs: 0, sm: 1 } }
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Export Excel</Box>
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 0.5 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => {
          setTabValue(newValue);
          if (newValue === 0) {
            dispatch(fetchSamples(filters));
          }
        }}>
          <Tab label="Active Samples" />
          {user?.role === 'Admin' && <Tab label="Deleted Samples" />}
        </Tabs>
      </Box>

      {tabValue === 0 ? (
        <>
          <Paper sx={{ mb: 0.5 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                cursor: { xs: 'pointer', md: 'default' },
                '&:hover': { xs: { bgcolor: 'action.hover' }, md: {} },
                borderRadius: 1,
                p: { xs: 0.5, md: 0 }
              }}
              onClick={() => isMobile && setShowFilters(!showFilters)}
            >
              <IconButton 
                sx={{ display: { xs: 'flex', md: 'none' }, mr: 1, pointerEvents: 'none' }}
              >
                <FilterList />
              </IconButton>
              <Typography variant="h6" sx={{ display: { xs: 'block', md: 'none' } }}>
                Filters {showFilters ? '(Expanded)' : '(Collapsed)'}
              </Typography>
            </Box>
            <Collapse in={showFilters || !isMobile}>
              <Grid container spacing={1} sx={{ px: 1, py: { md: 2 } }}>
                <Grid item xs={12} md={2.4}>
                  <TextField
                    label="Lab ID"
                    value={filters.labId}
                    onChange={(e) => handleFilterChange('labId', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <TextField
                    label="Sample Name"
                    value={filters.sampleName}
                    onChange={(e) => handleFilterChange('sampleName', e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <TextField
                    select
                    label="Status Filter"
                    value={filters.statusFilter}
                    onChange={(e) => handleFilterChange('statusFilter', e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="accepted">Accepted</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <TextField
                    select
                    label="Department"
                    value={filters.department}
                    onChange={(e) => handleFilterChange('department', e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="Chemical">Chemical</MenuItem>
                    <MenuItem value="Micro">Micro</MenuItem>
                    <MenuItem value="Instrument">Instrument</MenuItem>
                    <MenuItem value="GM">GM</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <TextField
                    select
                    label="Regularity"
                    value={filters.regularity}
                    onChange={(e) => handleFilterChange('regularity', e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="BIS">BIS</MenuItem>
                    <MenuItem value="FSSAI">FSSAI</MenuItem>
                    <MenuItem value="Food">Food</MenuItem>
                    <MenuItem value="General">General</MenuItem>
                    <MenuItem value="Subcontract">Subcontract</MenuItem>
                    <MenuItem value="IQC">IQC</MenuItem>
                    <MenuItem value="EIC">EIC</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Collapse>
          </Paper>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {samples.map((sample) => (
              <Card key={sample._id} sx={{ mb: 1.5, p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
                    {sample.labId}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => navigate(`/samples/${sample._id}`)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                    {canAcceptSample(sample) && (
                      <IconButton size="small" color="success" onClick={() => handleAcceptClick(sample)}>
                        <Check fontSize="small" />
                      </IconButton>
                    )}
                    {['Admin', 'Sample Collector'].includes(user?.role) && (
                      <IconButton size="small" onClick={() => handleEditClick(sample)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    )}
                    {user?.role === 'Admin' && (
                      <IconButton size="small" color="error" onClick={() => handleDeleteSample(sample._id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {sample.sampleName}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  <Chip label={sample.regularity || 'Not Set'} size="small" color="secondary" />
                  <Chip label={sample.storageTemperature || 'Not Set'} size="small" color="primary" />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {sample.departments.map(dept => (
                    <Chip key={dept} label={dept} size="small" variant="outlined" />
                  ))}
                </Box>
                <Box sx={{ mb: 1 }}>
                  {sample.departmentAssignments.map(da => (
                    <Box key={da.department} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <Typography variant="caption" sx={{ minWidth: 60 }}>{da.department}:</Typography>
                      <Chip label={da.status} color={getStatusColor(da.status)} size="small" />
                    </Box>
                  ))}
                </Box>
                {showAllDates && (
                  <>
                    <Typography variant="caption" color="text.secondary">
                      Created: {formatToIST(sample.createdDate)}
                    </Typography>
                    {sample.departmentAssignments.some(da => da.hodAcceptedAt) && (
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">Accepted:</Typography>
                        {sample.departmentAssignments
                          .filter(da => da.hodAcceptedAt)
                          .map(da => (
                            <Typography key={da.department} variant="caption" color="text.secondary" display="block">
                              {da.department}: {formatToIST(da.hodAcceptedAt)}
                            </Typography>
                          ))
                        }
                      </Box>
                    )}
                  </>
                )}
              </Card>
            ))}
          </Box>

          {/* Desktop Table View */}
          <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Lab ID</TableCell>
                  <TableCell>Sample Name</TableCell>
                  <TableCell>Regularity</TableCell>
                  <TableCell>Storage Temperature</TableCell>
                  <TableCell>Departments</TableCell>
                  <TableCell>Status Summary</TableCell>
                  {showAllDates && <TableCell>Accepted Date</TableCell>}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {samples.map((sample) => (
                  <TableRow key={sample._id}>
                    <TableCell>{sample.labId}</TableCell>
                    <TableCell>{sample.sampleName}</TableCell>
                    <TableCell>
                      <Chip 
                        label={sample.regularity || 'Not Set'} 
                        size="small" 
                        color={sample.regularity ? 'secondary' : 'default'}
                      />
                      {sample.regularity === 'BIS' && sample.bisSubcategories?.length > 0 && (
                        <Box sx={{ mt: 0.5 }}>
                          {sample.bisSubcategories.map(sub => (
                            <Chip key={sub} label={sub} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
                          ))}
                        </Box>
                      )}
                      {sample.regularity === 'FSSAI' && sample.fssaiCategory && (
                        <Box sx={{ mt: 0.5 }}>
                          <Chip label={sample.fssaiCategory} size="small" variant="outlined" />
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={sample.storageTemperature || 'Not Set'} 
                        size="small" 
                        color={sample.storageTemperature ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      {sample.departments.map(dept => (
                        <Chip key={dept} label={dept} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell>
                      {sample.departmentAssignments.map(da => (
                        <Box key={da.department} sx={{ mb: 0.5 }}>
                          <Typography variant="caption">{da.department}: </Typography>
                          <Chip
                            label={da.status}
                            color={getStatusColor(da.status)}
                            size="small"
                          />
                        </Box>
                      ))}
                    </TableCell>
                    {showAllDates && (
                      <TableCell>
                        {sample.departmentAssignments.some(da => da.hodAcceptedAt) ? (
                          <Box>
                            {sample.departmentAssignments
                              .filter(da => da.hodAcceptedAt)
                              .map(da => (
                                <Box key={da.department} sx={{ mb: 0.5 }}>
                                  <Typography variant="caption">{da.department}: </Typography>
                                  <Typography variant="body2">{formatToIST(da.hodAcceptedAt)}</Typography>
                                </Box>
                              ))
                            }
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary">Not Accepted</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => navigate(`/samples/${sample._id}`)}
                        color="primary"
                      >
                        <Visibility />
                      </IconButton>
                      {canAcceptSample(sample) && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => {
                            setSelectedSample(sample);
                            setAcceptData({ 
                              storageTemperature: sample.storageTemperature || ''
                            });
                            setAcceptDialog(true);
                            setActionError('');
                          }}
                        >
                          <Check />
                        </IconButton>
                      )}
                      {['Admin', 'Sample Collector'].includes(user?.role) && (
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            setEditSample(sample);
                            setEditDialog(true);
                          }}
                          color="primary"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {user?.role === 'Admin' && (
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteSample(sample._id)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={pagination.totalPages || 1}
              page={parseInt(pagination.currentPage) || 1}
              onChange={handlePageChange}
            />
          </Box>
        </>
      ) : (
        <DeletedSamples />
      )}

      {/* Accept Sample Dialog */}
      <Dialog open={acceptDialog} onClose={() => {
        setAcceptDialog(false);
        setSelectedSample(null);
        setAcceptData({ storageTemperature: '', sampleDisposition: 'Retain', disposedCollectedBy: '' });
        setActionError('');
      }}>
        <DialogTitle>Accept Sample</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          <Typography variant="body2" sx={{ mb: 2 }}>
            Sample: {selectedSample?.labId} - {selectedSample?.sampleName}
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Storage Temperature *</InputLabel>
            <Select
              value={acceptData.storageTemperature}
              onChange={(e) => setAcceptData(prev => ({ ...prev, storageTemperature: e.target.value }))}
              input={<OutlinedInput label="Storage Temperature *" />}
            >
              <MenuItem value="Ambient">Ambient</MenuItem>
              <MenuItem value="Freezed">Freezed</MenuItem>
              <MenuItem value="Deep Freezed">Deep Freezed</MenuItem>
            </Select>
          </FormControl>

        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAcceptDialog(false);
            setSelectedSample(null);
            setAcceptData({ storageTemperature: '' });
            setActionError('');
          }}>Cancel</Button>
          <Button 
            onClick={handleAcceptSample} 
            variant="contained"
            disabled={!acceptData.storageTemperature}
          >
            Accept
          </Button>
        </DialogActions>
      </Dialog>



      {/* Create Sample Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Sample</DialogTitle>
        <Formik
          initialValues={{
            labId: '',
            sampleName: '',
            storageTemperature: '',
            regularity: '',
            bisSubcategories: [],
            fssaiCategory: '',
            departments: []
          }}
          validationSchema={SampleSchema}
          onSubmit={async (values, { setSubmitting, setFieldError, resetForm }) => {
            try {
              await dispatch(createSample(values)).unwrap();
              setCreateDialog(false);
              resetForm();
              dispatch(fetchSamples(filters));
            } catch (error) {
              setSubmitting(false);
              if (error.includes('Lab ID') && error.includes('already exists')) {
                setFieldError('labId', error);
              } else {
                setActionError(error);
              }
            }
          }}
        >
          {({ errors, touched, isValid, dirty, values, setFieldValue, isSubmitting }) => (
            <Form>
              <DialogContent>
                {actionError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {actionError}
                  </Alert>
                )}
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="labId"
                      label="Lab ID *"
                      fullWidth
                      margin="normal"
                      error={touched.labId && errors.labId}
                      helperText={touched.labId && errors.labId}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      name="sampleName"
                      label="Sample Name *"
                      fullWidth
                      margin="normal"
                      error={touched.sampleName && errors.sampleName}
                      helperText={touched.sampleName && errors.sampleName}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal" error={touched.departments && errors.departments}>
                      <InputLabel>Departments *</InputLabel>
                      <Select
                        multiple
                        value={values.departments}
                        onChange={(e) => setFieldValue('departments', e.target.value)}
                        input={<OutlinedInput label="Departments *" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        <MenuItem value="Chemical">Chemical</MenuItem>
                        <MenuItem value="Micro">Micro</MenuItem>
                        <MenuItem value="Instrument">Instrument</MenuItem>
                        <MenuItem value="GM">GM</MenuItem>
                      </Select>
                      {touched.departments && errors.departments && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {errors.departments}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal" error={touched.storageTemperature && errors.storageTemperature}>
                      <InputLabel>Storage Temperature *</InputLabel>
                      <Select
                        value={values.storageTemperature}
                        onChange={(e) => setFieldValue('storageTemperature', e.target.value)}
                        input={<OutlinedInput label="Storage Temperature *" />}
                      >
                        <MenuItem value="Ambient">Ambient</MenuItem>
                        <MenuItem value="Freezed">Freezed</MenuItem>
                        <MenuItem value="Deep Freezed">Deep Freezed</MenuItem>
                      </Select>
                      {touched.storageTemperature && errors.storageTemperature && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {errors.storageTemperature}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth margin="normal" error={touched.regularity && errors.regularity}>
                      <InputLabel>Regularity *</InputLabel>
                      <Select
                        value={values.regularity}
                        onChange={(e) => {
                          setFieldValue('regularity', e.target.value);
                          setFieldValue('bisSubcategories', []);
                          setFieldValue('fssaiCategory', '');
                        }}
                        input={<OutlinedInput label="Regularity *" />}
                      >
                        <MenuItem value="BIS">BIS</MenuItem>
                        <MenuItem value="FSSAI">FSSAI</MenuItem>
                        <MenuItem value="Food">Food</MenuItem>
                        <MenuItem value="General">General</MenuItem>
                        <MenuItem value="Subcontract">Subcontract</MenuItem>
                        <MenuItem value="IQC">IQC</MenuItem>
                        <MenuItem value="IOPEPC">IOPEPC</MenuItem>
                        <MenuItem value="EIC">EIC</MenuItem>
                      </Select>
                      {touched.regularity && errors.regularity && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                          {errors.regularity}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  {values.regularity === 'BIS' && (
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>BIS Subcategories</InputLabel>
                        <Select
                          multiple
                          value={values.bisSubcategories}
                          onChange={(e) => setFieldValue('bisSubcategories', e.target.value)}
                          input={<OutlinedInput label="BIS Subcategories" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          <MenuItem value="Water">Water</MenuItem>
                          <MenuItem value="Nox Reduction Agent">Nox Reduction Agent</MenuItem>
                          <MenuItem value="Diaper">Diaper</MenuItem>
                          <MenuItem value="Sanitary">Sanitary</MenuItem>
                          <MenuItem value="FRK">FRK</MenuItem>
                          <MenuItem value="Cattle Feed">Cattle Feed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {values.regularity === 'FSSAI' && (
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>FSSAI Category</InputLabel>
                        <Select
                          value={values.fssaiCategory}
                          onChange={(e) => setFieldValue('fssaiCategory', e.target.value)}
                          input={<OutlinedInput label="FSSAI Category" />}
                        >
                          <MenuItem value="central">Central</MenuItem>
                          <MenuItem value="import">Import</MenuItem>
                          <MenuItem value="fbo">FBO</MenuItem>
                          <MenuItem value="state">State</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => { setCreateDialog(false); setActionError(''); }}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={!isValid || !dirty || isSubmitting}>
                  {isSubmitting ? 'Creating...' : 'Create Sample'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Edit Sample Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Sample</DialogTitle>
        {editSample && (
          <Formik
            initialValues={{
              labId: editSample.labId,
              sampleName: editSample.sampleName,
              departments: editSample.departments,
              storageTemperature: editSample.storageTemperature || '',
              regularity: editSample.regularity || '',
              bisSubcategories: editSample.bisSubcategories || [],
              fssaiCategory: editSample.fssaiCategory || ''
            }}
            validationSchema={SampleSchema}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                await api.put(`/samples/${editSample._id}`, values);
                setEditDialog(false);
                setEditSample(null);
                dispatch(fetchSamples(filters));
                refreshDashboardStats(); // Refresh dashboard stats after edit
              } catch (error) {
                setActionError(error.response?.data?.message || 'Failed to update sample');
                setSubmitting(false);
              }
            }}
          >
            {({ errors, touched, isValid, dirty, values, setFieldValue, isSubmitting }) => (
              <Form>
                <DialogContent>
                  {actionError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {actionError}
                    </Alert>
                  )}
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Field
                        as={TextField}
                        name="labId"
                        label="Lab ID"
                        fullWidth
                        margin="normal"
                        disabled
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Field
                        as={TextField}
                        name="sampleName"
                        label="Sample Name *"
                        fullWidth
                        margin="normal"
                        error={touched.sampleName && errors.sampleName}
                        helperText={touched.sampleName && errors.sampleName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Departments</InputLabel>
                        <Select
                          multiple
                          value={values.departments}
                          onChange={(e) => setFieldValue('departments', e.target.value)}
                          input={<OutlinedInput label="Departments" />}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {selected.map((value) => (
                                <Chip key={value} label={value} size="small" />
                              ))}
                            </Box>
                          )}
                        >
                          <MenuItem value="Chemical">Chemical</MenuItem>
                          <MenuItem value="Micro">Micro</MenuItem>
                          <MenuItem value="Instrument">Instrument</MenuItem>
                          <MenuItem value="GM">GM</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Storage Temperature</InputLabel>
                        <Select
                          value={values.storageTemperature}
                          onChange={(e) => setFieldValue('storageTemperature', e.target.value)}
                          input={<OutlinedInput label="Storage Temperature" />}
                        >
                          <MenuItem value="Ambient">Ambient</MenuItem>
                          <MenuItem value="Freezed">Freezed</MenuItem>
                          <MenuItem value="Deep Freezed">Deep Freezed</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth margin="normal">
                        <InputLabel>Regularity</InputLabel>
                        <Select
                          value={values.regularity}
                          onChange={(e) => {
                            setFieldValue('regularity', e.target.value);
                            setFieldValue('bisSubcategories', []);
                            setFieldValue('fssaiCategory', '');
                          }}
                          input={<OutlinedInput label="Regularity" />}
                        >
                          <MenuItem value="BIS">BIS</MenuItem>
                          <MenuItem value="FSSAI">FSSAI</MenuItem>
                          <MenuItem value="Food">Food</MenuItem>
                          <MenuItem value="General">General</MenuItem>
                          <MenuItem value="Subcontract">Subcontract</MenuItem>
                          <MenuItem value="IQC">IQC</MenuItem>
                          <MenuItem value="IOPEPC">IOPEPC</MenuItem>
                          <MenuItem value="EIC">EIC</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {values.regularity === 'BIS' && (
                      <Grid item xs={12}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>BIS Subcategories</InputLabel>
                          <Select
                            multiple
                            value={values.bisSubcategories}
                            onChange={(e) => setFieldValue('bisSubcategories', e.target.value)}
                            input={<OutlinedInput label="BIS Subcategories" />}
                            renderValue={(selected) => (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {selected.map((value) => (
                                  <Chip key={value} label={value} size="small" />
                                ))}
                              </Box>
                            )}
                          >
                            <MenuItem value="Water">Water</MenuItem>
                            <MenuItem value="Nox Reduction Agent">Nox Reduction Agent</MenuItem>
                            <MenuItem value="Diaper">Diaper</MenuItem>
                            <MenuItem value="Sanitary">Sanitary</MenuItem>
                            <MenuItem value="FRK">FRK</MenuItem>
                            <MenuItem value="Cattle Feed">Cattle Feed</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                    {values.regularity === 'FSSAI' && (
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth margin="normal">
                          <InputLabel>FSSAI Category</InputLabel>
                          <Select
                            value={values.fssaiCategory}
                            onChange={(e) => setFieldValue('fssaiCategory', e.target.value)}
                            input={<OutlinedInput label="FSSAI Category" />}
                          >
                            <MenuItem value="central">Central</MenuItem>
                            <MenuItem value="import">Import</MenuItem>
                            <MenuItem value="fbo">FBO</MenuItem>
                            <MenuItem value="state">State</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    )}
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => { setEditDialog(false); setEditSample(null); setActionError(''); }}>Cancel</Button>
                  <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
                    {isSubmitting ? 'Updating...' : 'Update Sample'}
                  </Button>
                </DialogActions>
              </Form>
            )}
          </Formik>
        )}
      </Dialog>

      {/* Bulk Upload Dialog */}
      <BulkUpload
        open={bulkUploadOpen}
        onClose={() => setBulkUploadOpen(false)}
        onSuccess={() => {
          setBulkUploadOpen(false);
          dispatch(fetchSamples(filters));
        }}
      />
    </Box>
  );
};

export default SampleList;